import { useState, useEffect } from "react";
import {
  Box,
  Text,
  Button,
  useToast,
  Spinner,
  Flex,
  VStack,
  Badge,
  Switch,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import useBusinessGuard from "../hooks/useBusinessGuard";
import SaturationThresholdToggle from "./SaturationThresholdToggle";
import PrintAllReadyButton from "./PrintAllReadyButton";

/**
 * DispatchReadySectors.jsx
 * Displays dispatch-eligible sectors with saturation filtering, dry-run simulation, and dispatch trigger.
 * Hydration-safe and scoped to authenticated business and campaign.
 */
export default function DispatchReadySectors({
  campaignId,
  region,
  group,
  onDryRunComplete,
}) {
  const toast = useToast();
  const { businessUUID, loading: authLoading } = useBusinessGuard(); // ✅ use UUID
  const [loading, setLoading] = useState(true);
  const [sectors, setSectors] = useState([]);
  const [autoDispatching, setAutoDispatching] = useState(false);
  const [saturationThreshold, setSaturationThreshold] = useState(0.5);
  const [useDryRun, setUseDryRun] = useState(false);

  useEffect(() => {
    if (authLoading || !businessUUID || !campaignId) return;

    const fetchReadySectors = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from("dispatch_queue")
          .select("*")
          .eq("business_id", businessUUID) // ✅ UUID fix
          .eq("campaign_id", campaignId)
          .is("scheduled_print_at", null);

        if (region) query = query.eq("region", region);
        if (group) query = query.eq("group_id", group);

        const { data, error } = await query;
        if (error) {
          throw new Error(error.message);
        }

        setSectors(data || []);
      } catch (err) {
        toast({
          title: "Error loading sectors",
          description: err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchReadySectors();
    const interval = setInterval(fetchReadySectors, 10000);
    return () => clearInterval(interval);
  }, [authLoading, businessUUID, campaignId, region, group, toast]);

  const filteredSectors = sectors.filter(
    (s) => s.saturation >= saturationThreshold
  );

  const handleDispatch = async () => {
    setAutoDispatching(true);
    try {
      const res = await fetch("/functions/v1/dispatchSectors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sectors: filteredSectors,
          business_id: businessUUID, // ✅ UUID fix
          campaign_id: campaignId,
          dry_run: useDryRun,
          region,
          group_id: group,
        }),
      });

      const result = await res.json();
      if (result.error) throw new Error(result.error);

      toast({
        title: useDryRun ? "Dry Run Complete" : "Dispatch Triggered",
        description: useDryRun
          ? `${filteredSectors.length} sectors simulated.`
          : `Successfully dispatched ${result.dispatched.length} sectors.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      if (useDryRun && result.dry_run_summary && onDryRunComplete) {
        onDryRunComplete(result.dry_run_summary);
      }
    } catch (err) {
      toast({
        title: "Dispatch Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAutoDispatching(false);
    }
  };

  if (authLoading || !businessUUID || !campaignId) return null;

  return (
    <Box p={4} bg="gray.50" _dark={{ bg: "gray.800" }} borderRadius="md">
      <Flex justify="space-between" align="center" mb={2}>
        <Text fontWeight="bold">📦 Dispatch-Ready Sectors</Text>
        <Badge colorScheme="purple" fontSize="xs">
          {filteredSectors.length} ready
        </Badge>
      </Flex>

      <SaturationThresholdToggle
        value={saturationThreshold}
        onChange={setSaturationThreshold}
      />

      <FormControl display="flex" alignItems="center" mt={4}>
        <FormLabel htmlFor="dry-run-toggle" mb="0" fontSize="sm">
          Dry Run Mode
        </FormLabel>
        <Switch
          id="dry-run-toggle"
          isChecked={useDryRun}
          onChange={(e) => setUseDryRun(e.target.checked)}
          colorScheme="orange"
        />
      </FormControl>

      {loading ? (
        <Flex justify="center" mt={6}>
          <Spinner />
        </Flex>
      ) : filteredSectors.length === 0 ? (
        <Text mt={4}>No sectors meet the current filters.</Text>
      ) : (
        <>
          <VStack align="start" spacing={2} mt={4}>
            {filteredSectors.map((s, i) => (
              <Box key={i}>
                <Text fontSize="sm">
                  {s.postcode} → {s.flyer_count} flyers / {s.household_count} households (
                  {(s.saturation * 100).toFixed(1)}%)
                </Text>
              </Box>
            ))}
          </VStack>

          <Flex justify="flex-end" gap={2} mt={4}>
            <PrintAllReadyButton businessId={businessUUID} /> {/* ✅ UUID fix */}
            <Button
              colorScheme={useDryRun ? "orange" : "green"}
              onClick={handleDispatch}
              isLoading={autoDispatching}
              loadingText={useDryRun ? "Simulating..." : "Dispatching..."}
            >
              {useDryRun ? "Dry Run Dispatch" : "Trigger Dispatch"}
            </Button>
          </Flex>
        </>
      )}
    </Box>
  );
}
